import { CallbackEvent, CallbackEventStatus } from "@/@types/platformSettings/callbacks";
import type {
  ApiCloudLimit,
  ApiCloudLimitPayload,
} from "@/@types/platformSettings/apiCloudLimit";
import httpManager from "@/app/shared/utils/httpManager";

class ApiService {
  private baseUrl = "/api/v1";

  async generateToken() {
    const url = `${this.baseUrl}/token/`;

    const response = await httpManager.get(url);

    return response.data?.token;
  }

  async generateMcpToken(body: { token_expiration: string; token_expiration_period: number }) {
    const url = `${this.baseUrl}/token/apicloud/`;

    const response = await httpManager.post(url, body);

    return response.data?.cloud_api_key;
  }

  async updateUrlCallback(urlCallback: string, token: string) {
    const url = `${this.baseUrl}/url-callback/`;

    await httpManager.post(url, { url_callback: urlCallback, token_callback: token });
  }

  async updateCallbackIntegration(newStatus: CallbackEventStatus) {
    const url = `${this.baseUrl}/status-callback/`;

    await httpManager.post(url, { status: newStatus });
  }

  async getCallbackEvents(): Promise<CallbackEvent[]> {
    const url = `${this.baseUrl}/callbacks/`;

    const response = await httpManager.get(url);

    return response.data;
  }

  async updateCallbackEvent(eventId: number, newStatus: CallbackEventStatus) {
    const url = `${this.baseUrl}/callbacks/${eventId}/`;

    await httpManager.put(url, { status: newStatus });
  }

  async updateMessageSendOption(newStatus: Boolean) {
    const url = `/settings/customer/`;

    await httpManager.put(url, { enable_enter: newStatus });
  }

  async updateSoundIncoming(enabledSoundMessage: Boolean) {
    const url = `/settings/customer/`;

    await httpManager.put(url, { enable_sound_message: enabledSoundMessage });
  }

  /**
   * Límite de consumo de API calls (postpago).
   *
   * Endpoints vivos en el backend: `GET`/`PUT /api/v1/api-cloud/limit/`. El `PUT` responde el
   * payload ya resuelto (monto en vigor, diferido y consumo), que es la fuente de la barra.
   * Referencia: `/home/lpalacios/beex/multitenant/odd/tasks/api-cloud-postpago-front.md`.
   */
  async getApiCloudLimit(): Promise<ApiCloudLimit> {
    const url = `${this.baseUrl}/api-cloud/limit/`;

    const response = await httpManager.get(url);

    return response.data;
  }

  async updateApiCloudLimit(payload: ApiCloudLimitPayload): Promise<ApiCloudLimit> {
    const url = `${this.baseUrl}/api-cloud/limit/`;

    const response = await httpManager.put(url, payload);

    return response.data;
  }
}

export const apiService = new ApiService();
